import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { getToken, setToken, clearToken, subscribeAuthSync } from "../lib/tokenStore";
import { getFcmToken } from "../lib/fcm";

const AuthContext = createContext(null);

function isCapacitorNative() {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

/**
 * Inside the Capacitor native app, register for native FCM/APNs as
 * soon as a user is signed in. Android auto-grants notification
 * permission for first-party apps so the visible "Enable
 * notifications" prompt never fires here — without this call the
 * device never asks the OS for a token and the backend has nothing
 * but the old Web Push subscription to deliver to (= Chrome
 * notifications).
 *
 * Re-runs cheaply on every auth refresh + app foreground. The backend
 * `/push/fcm/register` is idempotent, so re-posting the same token is
 * a no-op. We deliberately DO NOT short-circuit on `kb_fcm_token`
 * presence — if a previous registration silently failed (the token
 * never reached the backend), the local cache could lie to us forever
 * and the user would never get notifications. Re-registering on each
 * resume guarantees recovery.
 */
async function autoRegisterNativePush() {
  if (!isCapacitorNative()) return;
  try {
    await getFcmToken();
  } catch (e) {
    if (typeof console !== "undefined") {
      console.warn("[auth] autoRegisterNativePush failed:", e?.message || e);
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setSeller(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setSeller(data.seller);
      // Native app: silently register the device with FCM/APNs the
      // first time we have an authenticated user. Fire-and-forget — a
      // failure here never blocks the rest of auth.
      autoRegisterNativePush().catch(() => { /* best effort */ });
    } catch {
      clearToken({ broadcast: false });
      setUser(null);
      setSeller(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Cross-tab sync — if another tab on the SAME tenant logs in or out,
    // mirror that state here so the user doesn't see stale auth UI.
    const unsubscribe = subscribeAuthSync(() => { refresh(); });

    // Native app: re-attempt FCM/APNs registration every time the app
    // returns to foreground. Token registration is idempotent server-
    // side, so this is a no-op when the token is already known — but
    // critical when the first attempt failed (cold network, Firebase
    // not ready, race condition with auth refresh). Without this, a
    // single failed registration leaves the device unreachable forever.
    let appStateRemover = null;
    if (isCapacitorNative()) {
      try {
        const appPlugin = window?.Capacitor?.Plugins?.App;
        if (appPlugin && typeof appPlugin.addListener === "function") {
          const subPromise = appPlugin.addListener("appStateChange", (state) => {
            if (state?.isActive && getToken()) {
              autoRegisterNativePush().catch(() => { /* best effort */ });
            }
          });
          appStateRemover = () => { subPromise.then((s) => s.remove?.()).catch(() => {}); };
        }
      } catch { /* sandbox / plugin unavailable */ }
    }

    return () => {
      unsubscribe?.();
      appStateRemover?.();
    };
  }, []);

  const login = async (phone, code, name) => {
    const { data } = await api.post("/auth/otp/verify", { phone, code, name });
    setToken(data.token, data.user);
    setUser(data.user);
    // Super-admin tokens live in the __super__ slot, NOT the per-tenant slot.
    // refresh() runs `/auth/me` through the axios interceptor which picks
    // tokens by URL → if we're still on a tenant URL when login completes
    // (the storefront root, say), the interceptor would look at
    // kb_token:default, find nothing, and clear the (empty) slot — which
    // wipes the user we just set. Skip the redundant refresh; data.user
    // already has everything the super-admin console needs, and the navigation
    // to /superadmin will land on a URL where the __super__ slot is picked
    // for every subsequent request.
    if (data.user?.role !== "super_admin") {
      await refresh();
    }
    return data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setSeller(null);
  };

  return (
    <AuthContext.Provider value={{ user, seller, loading, login, logout, refresh, setSeller }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
